import type { SubscriptionOverviewResponse } from "@nlc/shared-types";
import type { ReactNode } from "react";

import { TicketIcon } from "lucide-react";

import { BankNote01Icon } from "@/assets/icons/BankNote01Icon";
import { dateFormatter } from "@/utils/dateFormatter";

import { formatCurrency } from "../../../utils/format-currency";

// The subscription backend does not expose discounts yet, so the card falls
// back to the discount shown in the Figma frame. Set it to null to preview the
// no-discount state, and drop the fallback once the endpoint fills
// upcomingPayment.discount.
const MOCK_DISCOUNT = { label: "10% off" };

export function UpcomingPaymentCard({
  data,
  renewalDateAction,
  showDiscount = true,
}: {
  data: Pick<SubscriptionOverviewResponse, "upcomingPayment">;
  renewalDateAction?: ReactNode;
  showDiscount?: boolean;
}) {
  const { upcomingPayment } = data;
  const discount = upcomingPayment.discount ?? MOCK_DISCOUNT;

  return (
    <div className="inline-flex flex-col justify-between gap-md rounded-xl border border-border-secondary bg-bg-primary py-xl px-3xl">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-xs">
          <h3 className="text-sm font-medium text-text-secondary">
            Upcoming payment
          </h3>
          <p className="text-4xl font-bold text-text-primary">
            {formatCurrency(upcomingPayment.amount, upcomingPayment.currency)}
          </p>
        </div>
        <div className="h-10 w-10 rounded-md bg-bg-brand-secondary flex items-center justify-center">
          <BankNote01Icon className="text-text-brand-primary" />
        </div>
      </div>

      <div className="mt-xl flex flex-col gap-xs">
        {showDiscount && discount && (
          <div className="flex items-center gap-xs text-text-success-primary">
            <TicketIcon size={16} />
            <span className="text-sm font-medium">{discount.label}</span>
          </div>
        )}
        <p className="text-sm text-text-secondary">
          Renewal date: {dateFormatter(upcomingPayment.renewalDate)}
        </p>
        {renewalDateAction}
      </div>
    </div>
  );
}
